import React from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {Content, EdgeSidebar, EdgeTrigger, Header, Root, SidebarContent} from '@mui-treasury/layout';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MenuIcon from '@mui/icons-material/Menu';
import {IconButton, ButtonBase, Container} from '@mui/material';
import {BrowserRouter, Redirect, Route, Switch} from 'react-router-dom';
import {AppSidenav} from '../../componentsOld/AppSidenav';
import JobEditPage from '../../componentsOld/jobs/JobEditPage';
import Modals from '../../componentsOld/Modals';
import PurchaseOrderEditPage from '../../componentsOld/purchase-orders/PurchaseOrderEditPage';
import {AuthorisedQuotePreview} from '../../componentsOld/quotes/AuthorisedQuotePreview';
import {QuoteEditPage} from '../../componentsOld/quotes/QuoteEditPage';
import Reporting from '../../componentsOld/reporting/Reporting';
import TrialSubscriptionModal from '../../componentsOld/TrialSubscriptionModal';
import UnpaidSubscriptionModal from '../../componentsOld/UnpaidSubscriptionModal';
import {AccessDenied, roles, PermissionBasedRoute, MultiSessionNotification} from '../../componentsHoops/AccessControl';
import ScheduleDashboard from '../../modules/schedule/ScheduleDashboard';
import TaskManyViewPage from '../../modules/task/TaskManyViewPage';
import TaskPage from '../../modules/task/TaskPage';
import theme from '../../ui/theme/index';
import Catalog from '../catalog';
import Customers from '../customers';
import PurchaseOrders from '../purchase-orders';
import QuotesContainer from '../quotes';
import Settings from '../settings';
import {SalesDocGuestPage, SalesDocBuilderPage, SalesBoardPage} from '../../pages/SalesDoc';
import {JobBoardPage} from '../../pages/Job';

const useStyles = makeStyles(() => ({
  header: {color: theme.palette.secondary.contrastText,},
  menuBtn: {
    background: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    margin: 4
  },
  collapseBtn: {
    color: '#fff',
    minWidth: 0,
    width: 40,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'rgba(0,0,0,0.24)',
    margin: '0 auto 16px',
    '&:hover': {backgroundColor: 'rgba(0,0,0,0.38)',},
  },
  sidebarContent: {
    border: 'none',
    overflowX: 'hidden'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  container: {
    padding: theme.spacing(3),
    flex: '1 1 0',
    display: 'flex !important',
    flexDirection: 'column',
    position: 'relative',
  },
}));

export const APP_BASENAME = '/';

export function AppRoutes() {
  const classes = useStyles();
  const company = useSelector((state) => state.companyReducer.company);
  const user = useSelector((state) => state.authReducer.userData);

  // this line removes the X scrollbar from body in the app
  document.body.style.overflowX = 'hidden';

  const {admin, standard, lowAccess} = roles;

  return (
    <BrowserRouter basename={APP_BASENAME}>
      <Root scheme={{
        header: {
          config: {
            xs: {
              position: 'relative',
              height: 56,
              backgroundColor: theme.palette.primary.main,
            },
          },
          hidden: ['md', 'lg', 'xl']
        },
        leftEdgeSidebar: {
          config: {
            xs: {
              variant: 'temporary',
              width: 'auto',
            },
            md: {
              variant: 'permanent',
              width: 256,
              collapsible: true,
              collapsedWidth: 64,
            },
          },
        },

      }}
      >
        <TrialSubscriptionModal />
        <UnpaidSubscriptionModal />
        <Modals />
        <MultiSessionNotification/>

        <Header
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.primary.main,
          }}
          elevation={2}
          className={classes.header}
        >
          <EdgeTrigger target={{anchor: 'left', field: 'open'}}>
            {(open, setOpen) => (
              <IconButton onClick={() => setOpen(!open)} size='large'>
                {open ? <KeyboardArrowLeftIcon /> : <MenuIcon className={classes.menuBtn} />}
              </IconButton>
            )}
          </EdgeTrigger>
        </Header>

        {/* Left Menu */}
        <EdgeSidebar anchor='left' sx={{'.MuiPaper-root': {boxShadow: 'none'}}}>
          {(props) => (
            <>
              <SidebarContent className={classes.sidebarContent}>
                <AppSidenav collapsed={props.state.leftEdgeSidebar.collapsed} />
              </SidebarContent>

              <EdgeTrigger target={{anchor: 'left', field: 'collapsed'}}>
                {(collapsed, setCollapsed) => (
                  <ButtonBase
                    onClick={() => setCollapsed(!collapsed)}
                    sx={{
                      minHeight: 40,
                      width: '100%',
                      bgcolor: 'grey.100',
                      borderTop: '1px solid',
                      borderColor: 'grey.200',
                    }}
                  >
                    {collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
                  </ButtonBase>
                )}
              </EdgeTrigger>
            </>
          )}
        </EdgeSidebar>

        {company._id && user._id &&
          <Content className={classes.content}>
            <Container maxWidth={false} className={`${classes.container} page-container`}>
              <Switch>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/catalog'>
                  <Catalog/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/customers'>
                  <Customers/>
                </PermissionBasedRoute>

                {/* Jobs */}
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/jobs/new' exact>
                  <JobEditPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path={['/jobs', '/jobs/sales/:salesDocNumber']} exact>
                  <JobBoardPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/jobs/:_id/edit' exact>
                  <JobEditPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/jobs/:jobId'>
                  <JobBoardPage/>
                </PermissionBasedRoute>

                {/* Quotes */}
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path='/quotes'>
                  <QuotesContainer/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path='/quotes/new'>
                  <QuoteEditPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path='/quotes/:quoteNumber/edit'>
                  <QuoteEditPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path={'/sales/job/:jobId'}>
                  <SalesBoardPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path='/sales/view/:salesDocId'>
                  <SalesDocGuestPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact
                                      path={[
                                        '/sales/new',
                                        '/sales/new/:templateId',
                                        '/sales/edit/:salesDocNumber',
                                        '/sales/edit/:salesDocNumber/revision/:revisionId',
                                        '/sales/draft/:draftId',
                                        '/sales/:action/:salesDocNumber',
                                      ]}>
                  <SalesDocBuilderPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} exact path={['/sales', '/sales/:salesDocNumber', '/sales/:salesDocId']}>
                  <SalesBoardPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} path={['/quotes/:quoteId/view', '/app/quotes/:quoteId/view']}>
                  <AuthorisedQuotePreview/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} path={['/quotes/:quoteId/comments', '/app/quotes/:quoteId/comments']}>
                  <AuthorisedQuotePreview/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} path={['/quotes/:quoteId/files', '/app/quotes/:quoteId/files']}>
                  <AuthorisedQuotePreview/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} path={['/quotes/:quoteId/tasks', '/app/quotes/:quoteId/tasks']}>
                  <AuthorisedQuotePreview/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard]} path={['/quotes/:quoteId/activity', '/app/quotes/:quoteId/activity']}>
                  <AuthorisedQuotePreview/>
                </PermissionBasedRoute>

                {/* Purchase Orders */}
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/purchase-orders'>
                  <PurchaseOrders/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/purchase-orders/new'>
                  <PurchaseOrderEditPage/>
                </PermissionBasedRoute>

                {/* Settings */}
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path='/settings'>
                  <Settings/>
                </PermissionBasedRoute>

                {/* Tasks */}
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} exact path={'/tasks'}>
                  <TaskManyViewPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} path={['/tasks/:entityId', '/app/tasks/:entityId']}>
                  <TaskPage/>
                </PermissionBasedRoute>
                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} exact path={'/schedule'}>
                  <ScheduleDashboard/>
                </PermissionBasedRoute>
                <Route path='/accessdenied'>
                  <AccessDenied/>
                </Route>
                <PermissionBasedRoute allowedRoles={[admin]} path={'/reporting'}>
                  <Reporting/>
                </PermissionBasedRoute>

                <PermissionBasedRoute allowedRoles={[admin, standard, lowAccess]} exact path={'/schedule'}>
                  <ScheduleDashboard/>
                </PermissionBasedRoute>
                {/* This redirect adjusts a url that comes out of the comments project (repo in git) once the url is changed the normal route security conditions apply */}
                <Redirect from='/quoteV2s/:salesDocId' to='/sales/:salesDocId' />
                {/*  this is the login redirect */}
                {/*  add some comment to easy find later. */}
                {/*  login( */}
                {/*  history.push('/jobs') */}
                <Redirect to={'/jobs'}/>
              </Switch>
            </Container>
          </Content>
        }
      </Root>
    </BrowserRouter>
  );
}
